import React from "react";
import {
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Typography,
  Box,
  Chip,
  LinearProgress,
  Link,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";
import { Notification } from "../../interfaces";
import { useRelativeTime } from "./hook/useRelativeTime";

const categoryStyles: Record<
  string,
  { color: string; bg: string; label: string; icon: React.ReactNode }
> = {
  info: {
    color: "#2563eb",
    bg: "#e8f0fe",
    label: "Info",
    icon: <InfoOutlinedIcon fontSize="small" />,
  },
  warning: {
    color: "#b45309",
    bg: "#fef3e2",
    label: "Warning",
    icon: <WarningAmberOutlinedIcon fontSize="small" />,
  },
  error: {
    color: "#dc2626",
    bg: "#fde8e8",
    label: "Error",
    icon: <ErrorOutlineIcon fontSize="small" />,
  },
  success: {
    color: "#15803d",
    bg: "#e7f6ec",
    label: "Success",
    icon: <CheckCircleOutlineIcon fontSize="small" />,
  },
};

const NotificationItem: React.FC<{
  notification: Notification;
  markAsRead: (id: number) => void;
}> = ({ notification, markAsRead }) => {
  const navigate = useNavigate();
  // In-app routes (e.g. "/management/agents", "/suites") navigate in place;
  // anything else (report blobs / external URLs) opens in a new tab.
  const isInternalLink = !!notification.attachment_url?.startsWith("/");
  const category =
    categoryStyles[notification.category || "info"] || categoryStyles.info;
  const relativeTime = useRelativeTime(notification.created_at);
  const isHigh = notification.priority === "high";
  const unread = notification.is_read === false;

  return (
    <ListItem
      alignItems="flex-start"
      onClick={() => markAsRead(notification.id)}
      sx={{
        py: 1.5,
        px: 2,
        gap: 1.5,
        cursor: "pointer",
        position: "relative",
        transition: "background-color 120ms ease",
        "&:hover": { bgcolor: "background.level1" },
        ...(unread && {
          bgcolor: `${category.color}0a`,
        }),
      }}
    >
      {unread && (
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 3,
            bgcolor: category.color,
          }}
        />
      )}
      <ListItemAvatar sx={{ minWidth: 0, mt: 0.5 }}>
        <Avatar
          variant="rounded"
          sx={{
            bgcolor: category.bg,
            color: category.color,
            width: 38,
            height: 38,
          }}
        >
          {category.icon}
        </Avatar>
      </ListItemAvatar>

      <ListItemText
        disableTypography
        primary={
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              mb: 0.25,
            }}
          >
            <Typography
              sx={{
                flex: 1,
                fontSize: "0.875rem",
                fontWeight: unread ? 700 : 600,
                lineHeight: 1.35,
              }}
            >
              {notification.title}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", whiteSpace: "nowrap", mt: "1px" }}
            >
              {relativeTime}
            </Typography>
          </Box>
        }
        secondary={
          <Box>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontSize: "0.8rem" }}
            >
              {notification.message?.length > 120
                ? `${notification.message.substring(0, 120)}…`
                : notification.message}
            </Typography>

            {notification.type === "progress" &&
              notification.progress !== undefined && (
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}
                >
                  <LinearProgress
                    variant="determinate"
                    value={notification.progress}
                    sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {notification.progress}%
                  </Typography>
                </Box>
              )}

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 1 }}>
              <Chip
                size="small"
                label={category.label}
                sx={{
                  bgcolor: category.bg,
                  color: category.color,
                  fontWeight: 700,
                }}
              />
              {isHigh && (
                <Chip
                  size="small"
                  label="High priority"
                  sx={{ bgcolor: "#fde8e8", color: "#dc2626", fontWeight: 700 }}
                />
              )}
              {notification.attachment_url &&
                (isInternalLink ? (
                  <Link
                    component="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      markAsRead(notification.id);
                      navigate(notification.attachment_url as string);
                    }}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.25,
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      color: "primary.main",
                      border: "none",
                      background: "none",
                      cursor: "pointer",
                      p: 0,
                    }}
                  >
                    View <ArrowForwardIcon sx={{ fontSize: 14 }} />
                  </Link>
                ) : (
                  <Link
                    href={notification.attachment_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.25,
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      color: "primary.main",
                    }}
                  >
                    Open report <OpenInNewIcon sx={{ fontSize: 14 }} />
                  </Link>
                ))}
            </Box>
          </Box>
        }
      />
    </ListItem>
  );
};

export default NotificationItem;
